import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Heart, Mail, Phone, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useFavorites } from "@/hooks/useFavorites";

function ContactSeller({ listing, user }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [body, setBody] = useState("");

  const send = async () => {
    if (!user) { navigate("/login"); return; }
    if (!body.trim()) return;
    try {
      await api.post("/messages", { listing_id: listing.listing_id, to_user_id: listing.owner_id, body });
      toast.success("Message sent");
      navigate("/messages");
    } catch {
      toast.error("Could not send message");
    }
  };

  return (
    <>
      <button data-testid="contact-seller-btn" onClick={() => setOpen((o) => !o)} className="bg-black text-white px-4 py-3 border-2 border-black font-mono-data text-sm uppercase hover:bg-[#0000EE] hover:border-[#0000EE] flex items-center justify-center gap-2">
        <MessageSquare size={14} /> Contact Seller
      </button>
      {open && (
        <div className="border-2 border-black p-3 mt-1 space-y-2 bg-[#FFFEEA]" data-testid="contact-form">
          <textarea data-testid="contact-msg-input" value={body} onChange={(e) => setBody(e.target.value)} rows={4} placeholder="Hi, is this still available?" className="w-full border-2 border-black p-2 font-mono-data text-sm rounded-none focus:outline-none focus:ring-2 focus:ring-[#0000EE]" />
          <button data-testid="send-msg-btn" onClick={send} className="w-full bg-[#0000EE] text-white px-4 py-2 border-2 border-[#0000EE] font-mono-data text-xs uppercase hover:bg-[#0000AA] hover:border-[#0000AA]">Send Message</button>
          {listing.contact_email && <div className="font-mono-data text-xs flex items-center gap-1"><Mail size={12} /> {listing.contact_email}</div>}
          {listing.contact_phone && <div className="font-mono-data text-xs flex items-center gap-1"><Phone size={12} /> {listing.contact_phone}</div>}
        </div>
      )}
    </>
  );
}

function FavoriteButton({ listingId, user }) {
  const navigate = useNavigate();
  const { favs, toggle } = useFavorites(user);
  const isFav = favs.includes(listingId);

  const onClick = async () => {
    if (!user) { navigate("/login"); return; }
    await toggle(listingId);
    toast.success(isFav ? "Removed from favorites" : "Saved to favorites");
  };

  return (
    <button data-testid="favorite-btn" onClick={onClick} className={`px-4 py-3 border-2 border-black font-mono-data text-sm uppercase flex items-center justify-center gap-2 ${isFav ? "bg-[#FF0000] text-white border-[#FF0000]" : "bg-white text-black hover:bg-yellow-100"}`}>
      <Heart size={14} fill={isFav ? "#fff" : "none"} /> {isFav ? "Saved" : "Save to Favorites"}
    </button>
  );
}

export function ListingActions({ listing, user }) {
  const isOwner = user && user.user_id === listing.owner_id;
  return (
    <div className="p-4 border-t border-black flex flex-col gap-2">
      {isOwner ? (
        <Link to={`/edit/${listing.listing_id}`} data-testid="edit-listing-btn" className="bg-black text-white px-4 py-3 border-2 border-black text-center font-mono-data text-sm uppercase hover:bg-[#0000EE] hover:border-[#0000EE] no-underline hover:no-underline">Edit Listing</Link>
      ) : (
        <>
          <ContactSeller listing={listing} user={user} />
          <FavoriteButton listingId={listing.listing_id} user={user} />
        </>
      )}
    </div>
  );
}
